import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Video, ArrowLeft, Calendar, Users, Clock, Globe, ChefHat, 
  Play, Bell, MessageSquare, Send, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GlobalKitchenLive() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState({ live_now: [], upcoming: [] });
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [legacyMoment, setLegacyMoment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [registering, setRegistering] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/api/food/kitchen/live`);
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const registerForSession = async (sessionId) => {
    if (!user) {
      toast.error('Please log in to register for sessions');
      return;
    }

    setRegistering(sessionId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/food/kitchen/register/${sessionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.status === 'already_registered') {
          toast.info("You're already registered for this session!");
        } else {
          toast.success(`Registered for ${data.session}!`);
          if (data.badges_earned?.length) {
            toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
          }
        }
        fetchSessions();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to register');
      }
    } catch (err) {
      toast.error('Failed to register');
    } finally {
      setRegistering(null);
    }
  };

  const joinSession = async (sessionId) => {
    if (!user) {
      toast.error('Please log in to join sessions');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/food/kitchen/sessions/${sessionId}/join`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setActiveSession({
          ...sessions.live_now.find(s => s.id === sessionId),
          ...data
        });
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to join session');
      }
    } catch (err) {
      toast.error('Failed to join session');
    }
  };

  const submitLegacyMoment = async () => {
    if (!legacyMoment.trim() || !activeSession) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/food/kitchen/sessions/${activeSession.id}/legacy-moment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: activeSession.id,
          response: legacyMoment
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        setLegacyMoment('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit');
      }
    } catch (err) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // Active Session View
  if (activeSession) {
    return (
      <div className="min-h-screen bg-stone-900" data-testid="kitchen-live-session">
        {/* Video Player Area */}
        <div className="relative aspect-video bg-black max-h-[60vh]">
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-orange-900/50 to-stone-900">
            <div className="text-center">
              <Video className="h-20 w-20 text-orange-400 mx-auto mb-4" />
              <p className="text-white text-xl font-semibold">{activeSession.session}</p>
              <p className="text-orange-300">Live cooking session in progress</p>
            </div>
          </div>
          <div className="absolute top-4 left-4">
            <Badge className="bg-red-500 text-white animate-pulse">LIVE</Badge>
          </div>
          <Button
            onClick={() => setActiveSession(null)}
            variant="ghost"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Exit
          </Button>
        </div>

        {/* Session Info & Legacy Moment */}
        <div className="container mx-auto px-4 py-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Session Details */}
            <div className="lg:col-span-2">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ChefHat className="h-5 w-5 text-orange-400" />
                    {activeSession.chef?.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-orange-200 mb-4">
                    <Globe className="h-4 w-4 inline mr-2" />
                    {activeSession.chef?.region}
                  </p>
                  <div className="bg-orange-500/10 border border-orange-400/30 rounded-lg p-4">
                    <h3 className="text-orange-300 font-semibold mb-2 flex items-center">
                      <Heart className="h-4 w-4 mr-2" />
                      Today's Legacy Moment
                    </h3>
                    <p className="text-white italic">"{activeSession.legacy_moment_prompt}"</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Legacy Moment Submission */}
            <div>
              <Card className="bg-gradient-to-br from-orange-600/20 to-amber-600/20 border-orange-400/30">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Share Your Story</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={legacyMoment}
                    onChange={(e) => setLegacyMoment(e.target.value)}
                    placeholder="Share your food memory or family tradition..."
                    className="bg-white/5 border-white/10 text-white mb-4"
                    rows={4}
                  />
                  <Button
                    onClick={submitLegacyMoment}
                    disabled={!legacyMoment.trim() || submitting}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {submitting ? 'Sharing...' : 'Share with Community'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-900 to-stone-900" data-testid="global-kitchen-live">
      {/* Header */}
      <div className="border-b border-orange-500/30 py-12">
        <div className="container mx-auto px-4">
          <Link to="/food" className="inline-flex items-center text-orange-300 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Farm2Table
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-4 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl">
              <Video className="h-10 w-10 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">Global Kitchen Live</h1>
              <p className="text-orange-200">Join live cooking sessions from chefs around the world</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Live Now Section */}
        {sessions.live_now?.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <span className="w-4 h-4 bg-red-500 rounded-full animate-pulse mr-3"></span>
              Live Now
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {sessions.live_now.map((session, idx) => (
                <motion.div
                  key={session.id || idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="bg-gradient-to-br from-red-600/30 to-orange-600/30 border-red-400/50 overflow-hidden group cursor-pointer hover:border-red-400 transition-all">
                    <div className="aspect-video bg-gradient-to-br from-orange-800 to-red-900 relative flex items-center justify-center">
                      <Video className="h-16 w-16 text-white/50" />
                      <Badge className="absolute top-3 left-3 bg-red-500 text-white animate-pulse">
                        LIVE
                      </Badge>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button onClick={() => joinSession(session.id)} className="bg-white text-red-600 hover:bg-red-50">
                          <Play className="h-5 w-5 mr-2" /> Join Now
                        </Button>
                      </div>
                    </div>
                    <CardContent className="py-4">
                      <Badge className="bg-orange-500/20 text-orange-300 mb-2">{session.cultural_focus}</Badge>
                      <h3 className="font-semibold text-white text-lg">{session.title}</h3>
                      <p className="text-orange-200 text-sm mt-1">
                        <ChefHat className="h-4 w-4 inline mr-1" />
                        {session.chef?.name} • {session.chef?.region}
                      </p>
                      <p className="text-orange-300 text-sm mt-2">
                        <Users className="h-4 w-4 inline mr-1" />
                        {session.registered || 0} viewers
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Sessions */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">Upcoming Sessions</h2>
          {sessions.upcoming?.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-16 text-center">
                <Calendar className="h-16 w-16 text-orange-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Upcoming Sessions</h3>
                <p className="text-orange-200">Check back soon for new cooking sessions!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sessions.upcoming.map((session, idx) => (
                <motion.div
                  key={session.id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="h-full bg-white/5 backdrop-blur border-white/10 hover:border-orange-400/50 transition-all">
                    <CardContent className="py-5">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className={session.tier === 'premium' ? 'bg-purple-500' : 'bg-emerald-500'}>
                          {session.tier === 'premium' ? 'Premium' : 'Free'}
                        </Badge>
                        <Badge variant="outline" className="border-orange-400/50 text-orange-200">
                          {session.cultural_focus}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-white text-lg mb-2">{session.title}</h3>
                      <div className="space-y-2 text-sm text-orange-300 mb-4">
                        <p><ChefHat className="h-4 w-4 inline mr-2" />{session.chef?.name}</p>
                        <p><Globe className="h-4 w-4 inline mr-2" />{session.chef?.region}</p>
                        <p><Calendar className="h-4 w-4 inline mr-2" />
                          {new Date(session.date || session.scheduled_date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                        <p><Users className="h-4 w-4 inline mr-2" />
                          {session.registered || 0} / {session.capacity} registered
                        </p>
                      </div>
                      <Button
                        onClick={() => registerForSession(session.id)}
                        disabled={registering === session.id}
                        className="w-full bg-orange-600 hover:bg-orange-700"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        {registering === session.id ? 'Registering...' : 'Register'}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
